import { toast } from '@ethsign/ui';

import { ethPublicKeyEncrypt, ethSignAesEncrypt, shortenWalletAddress } from '@ethsign/utils-web';

import localforage from 'localforage';

import { filter, find, intersectionWith, isEmpty } from 'lodash-es';

import { createContact, createContract, getBatchPublicKeys, queryContractTemplate } from '@/services';
import { html2pdf } from '@/services/document';
import { IContact, ICurrentUser, SendType, SIGN_MODE_ENUM } from '@/types';
import { IAddFields } from '@/types/template-type';
import {
  fileToString,
  getFileExtension,
  getNetworkByAddress,
  getWrapHtml,
  isSameUser,
  isValidEmail,
  reportGAEvent,
  transformAddress,
  WalletType,
  walletValidators
} from '@/utils';
import { ControllerBase } from '@/utils/bizify';

export interface PageData {
  currentUser: any;
  expireDate?: any;
  password: string;
  enableExpireDate: boolean;
  enablePassword: boolean;
  annotation: string;
  passwordModalVisible: boolean;
  recipients: IContact[];
  viewers: IContact[];
  currentStep: number;
  file: any;
  annotations: any[];
  xfdfString: string;
  contractName: string;
  [key: string]: any;
  loading?: boolean;
  extraMessage: string;
  signerValue?: any[];
  fileType: 'pdf' | 'word';
  sendType: SendType;
  contractType: SIGN_MODE_ENUM;
  contractSendTypeModalOpen: boolean;
  shareModalVisible: boolean;
  contractId: string;
  expireTimeError: boolean;
  templateData?: IAddFields;
}

const isEmailDuplicate = (a: Partial<IContact>, b: Partial<IContact>): boolean => {
  return !!a.email && !!b.email && a.email.toLowerCase() === b.email.toLowerCase();
};

const isWalletDuplicate = (a: Partial<IContact>, b: Partial<IContact>): boolean => {
  return !!a.walletAddress && !!b.walletAddress && a.walletAddress.toLowerCase() === b.walletAddress.toLowerCase();
};

export class PageCtrl extends ControllerBase<PageData> {
  protected $data(): PageData {
    return {
      currentUser: '',
      expireDate: undefined,
      password: '',
      enableExpireDate: false,
      enablePassword: true,
      annotation: '',
      passwordModalVisible: false,
      recipients: [],
      currentStep: 0,
      annotations: [],
      xfdfString: '',
      file: null,
      contractName: '',
      loading: false,
      extraMessage: '',
      signerValue: [],
      fileType: 'pdf',
      sendType: SendType.invite,
      contractSendTypeModalOpen: false,
      contractType: SIGN_MODE_ENUM.OneSignOne,
      shareModalVisible: false,
      contractId: '',
      expireTimeError: false,
      viewers: []
    };
  }

  setContractSendTypeModalOpen = (open: boolean): void => {
    this.state.contractSendTypeModalOpen = open;
  };
  setPasswordModalVisible = (passwordModalVisible: boolean): void => {
    this.state.passwordModalVisible = passwordModalVisible;
  };

  setShareModalVisible = (shareModalVisible: boolean): void => {
    this.state.shareModalVisible = shareModalVisible;
  };

  setSignerValue = (signerValue: any[]): void => {
    this.state.signerValue = signerValue;
  };

  setExpireTimeError = (expireTimeError: boolean): void => {
    this.state.expireTimeError = expireTimeError;
  };

  setData = (data: Partial<PageData>): void => {
    Object.keys(data).forEach((key: string) => {
      this.state[key] = data[key as keyof PageData];
    });
  };
  saveContract = async (data: IContact): Promise<void> => {
    await createContact(data);
  };
  onCreate = async ({
    user,
    file,
    folderId,
    temId,
    templateId
  }: {
    user: ICurrentUser | undefined;
    file: any;
    folderId: string;
    temId: string;
    templateId: string;
  }): Promise<boolean> => {
    let xfdfString = this.state.xfdfString;
    xfdfString = JSON.stringify(
      JSON.parse(xfdfString).map((item: any) => {
        delete item.bitmapFile;
        return item;
      })
    );
    let encryptedContractPassword = '';
    try {
      const fileInfo = JSON.stringify({
        fileString: await fileToString(file),
        annotation:
          this.state.contractType === SIGN_MODE_ENUM.AllSignOne
            ? ''
            : xfdfString.replaceAll(/<imagedata>\S+<\/imagedata>/gi, ''),
        title: file.filename
      });

      let publicKeys: { [key: string]: string } = {};
      if (user?.enableIdentity && this.state.enablePassword) {
        if (this.state.sendType === SendType.invite) {
          const publicKeysRes = await getBatchPublicKeys({
            addresses: this.state.recipients.map(
              (it: any) =>
                (it.network === WalletType.EVM
                  ? transformAddress(it.walletAddress, WalletType.EVM)
                  : it.walletAddress) || ''
            )
          });
          publicKeys = publicKeysRes?.users.reduce((a: any, b: any) => {
            a[b.address] = b.identityPublicKey;
            return a;
          }, {});
        }
        encryptedContractPassword = await ethPublicKeyEncrypt(user.identityPublicKey, this.state.password);
      }
      const res = {
        folderId: folderId ? Number(folderId) : undefined,
        title: this.state.contractName,
        fileEncrypted: this.state.enablePassword ? await ethSignAesEncrypt(fileInfo, this.state.password) : fileInfo,
        enableEncryption: this.state.enablePassword,
        encryptedContractPassword,
        recipients:
          this.state.sendType === SendType.link
            ? []
            : await Promise.all(
                this.state.recipients?.map(async (it: any) => {
                  const publicKey = publicKeys[it.walletAddress];
                  const address = transformAddress(it.walletAddress, it.network);
                  return {
                    nickName: it.name,
                    address,
                    role: 'signer',
                    chainType: address ? it.network : undefined,
                    email: it.email,
                    telegram: it.telegram,
                    encryptedContractPassword: publicKey
                      ? await ethPublicKeyEncrypt(publicKey, this.state.password)
                      : undefined
                  };
                })
              ),
        enableExpiration: this.state.sendType === 'link' ? true : this.state.enableExpireDate,
        extraMessage: this.state.extraMessage,
        expireTime:
          this.state.sendType === 'link'
            ? new Date(this.state.expireDate!).getTime()
            : this.state.enableExpireDate && this.state.expireDate
            ? new Date(this.state.expireDate).getTime()
            : undefined,
        signMode: this.state.sendType === SendType.link ? this.state.contractType : undefined,
        templateId: templateId ? Number(templateId) : undefined,
        mailto: this.state.viewers.map((it: any) => it.email).join(',')
      };
      this.state.loading = true;

      const { contractId } = await createContract(res);
      reportGAEvent('pc_contract_sendContract', {
        address: user!.address,
        network: user!.chainType,
        walletName: user!.authType,
        contractId,
        tempId: temId,
        contractName: this.state.contractName,
        recipients: this.state.recipients.map((it: any) => ({
          nickName: it.name,
          address: transformAddress(it.walletAddress, it.network),
          role: 'signer',
          chainType: it.network,
          email: it.email,
          telegram: it.telegram
        }))
      });
      this.state.contractId = contractId;
      this.state.loading = false;
      return true;
    } catch (error) {
      console.log(error);

      this.state.loading = false;
      return false;
    }
  };
  onUpload = async (file: File, user: ICurrentUser, id: string): Promise<void> => {
    const myFile: any = file;

    localforage.config({
      driver: localforage.INDEXEDDB
    });
    await localforage.setItem(id, [myFile]);
    if (!file) return;
    if (getFileExtension(file.name) !== 'pdf') {
      this.state.fileType = 'word';
      // myFile = { name: myFile.name, html: data };
    } else {
      this.state.fileType = 'pdf';
    }
    reportGAEvent('pc_contract_uploadContract', {
      address: user.address,
      fileName: file.name,
      tempId: id
    });
  };

  // 验证signers 数组是否合法
  isValidateSigners = (signers: Partial<IContact>[]): boolean => {
    return signers?.every((item: any) => {
      if (!item.walletAddress && !item.email) return false;
      let validateState = false;
      if (item.walletAddress) {
        validateState = item.network
          ? walletValidators[item.network as WalletType](item.walletAddress)
          : !!getNetworkByAddress(item.walletAddress);
        if (!validateState) {
          toast({
            title: 'Please enter a valid  wallet address',
            variant: 'error',
            description: 'Please enter a valid wallet address so we can send the contract to the right person.'
          });
        }
      }
      if (item.email) {
        validateState = isValidEmail(item.email);
        if (!validateState) {
          toast({
            title: 'Please enter a valid email address',
            variant: 'error',
            description: 'Please enter a valid email so we can send the contract to the right person.'
          });
        }
      }
      return validateState;
    });
  };

  // 验证新增数据是否有重复
  validateDuplicateNewSigner = (data: IContact[], newSigners: any[]): boolean => {
    const duplicateEmailUser = intersectionWith(data, newSigners, isEmailDuplicate);
    const duplicateWalletUser = intersectionWith(data, newSigners, isWalletDuplicate);
    if (duplicateEmailUser?.length) {
      toast({
        title: 'Duplicated Entries',
        description: `Seems like you have already added ${duplicateEmailUser[0].email} as a signer. `,
        variant: 'error'
      });
      return false;
    }
    if (duplicateWalletUser?.length) {
      toast({
        title: 'Duplicated Entries',
        description: `Seems like you have already added ${duplicateWalletUser[0].walletAddress} as a signer. `,
        variant: 'error'
      });
      return false;
    }
    return true;
  };

  // 验证整个列表是否有重复
  validateDuplicateSigners = (data: Partial<IContact>[]): boolean => {
    const duplicateEmailUser = find(data, (item, _index, array) => {
      return filter(array, (it) => isEmailDuplicate(item, it)).length > 1;
    });
    const duplicateWalletUser = find(data, (item, _index, array) => {
      return filter(array, (it) => isWalletDuplicate(item, it)).length > 1;
    });
    if (duplicateEmailUser) {
      toast({
        title: 'Duplicated Entries',
        description: `Seems like signer ${duplicateEmailUser.email} has duplicated entries`,
        variant: 'error'
      });
      return false;
    }

    if (duplicateWalletUser) {
      toast({
        title: 'Duplicated Entries',
        description: `Seems like signer ${duplicateWalletUser.walletAddress} has duplicated entries`,
        variant: 'error'
      });
      return false;
    }

    return true;
  };

  // 判断除去数组第i个元素外，剩余元素的field字段是否有重复
  isDuplicateRecord = (
    record: Partial<IContact>,
    i: number,
    data: Partial<IContact>[],
    field: keyof IContact
  ): boolean => {
    const duplicateUser = data.filter((item, index) => {
      return index !== i && record[field] && item[field] && item[field] === record[field];
    });
    console.log(data, i, duplicateUser, record, field);
    if (duplicateUser.length) {
      toast({
        title: 'Duplicated Entries',
        description: `Seems like signer ${record[field]} has duplicated entries`,
        variant: 'error'
      });
    }
    return !!duplicateUser.length;
  };
  getStep = (step: number): number => {
    const { sendType, templateData } = this.state;
    return (sendType === SendType.invite ? step : step - 1) + (templateData ? 1 : 0);
  };
  onNext = async ({
    file,
    sendType,
    setFile,
    id
  }: {
    file: any;
    sendType?: SendType;
    setFile: any;
    id: string;
  }): Promise<any> => {
    if (this.state.currentStep === 0) {
      if (!file) {
        toast({
          title: 'Error',
          description: 'Please upload contract',
          variant: 'error'
        });
        return false;
      }
      this.state.contractSendTypeModalOpen = !sendType;
      return !!sendType;
    }
    if (this.state.templateData && this.state.currentStep === 1) {
      const unCompletedItem = this.state.templateData.fields
        ?.filter((item) => item.type === 'term')
        .find((item) => item.required && isEmpty(item.value));
      if (unCompletedItem) {
        toast({
          title: 'Error',
          description: `Please fill in ${unCompletedItem.label}`,
          variant: 'error'
        });
        return false;
      }
      this.state.loading = true;
      try {
        const file = await this.generatePdf(this.state.templateData.html);
        setFile(file);
        localforage.config({
          driver: localforage.INDEXEDDB
        });
        await localforage.setItem(id, file);
      } catch (error) {
        this.state.loading = false;

        return false;
      }
      this.state.loading = false;
      return true;
    }
    if (this.state.currentStep === this.getStep(1)) {
      if (!this.state.recipients?.length) {
        toast({
          title: 'Error',
          description: 'Please add at least one signer',
          variant: 'error'
        });
        return false;
      }
      const isAllValidate = this.isValidateSigners(this.state.recipients);
      if (!isAllValidate) {
        toast({
          title: 'Error',
          description: 'Please add correct email or wallet address and network for all signers',
          variant: 'error'
        });
        return false;
      }
      if (!this.validateDuplicateSigners(this.state.recipients)) {
        return false;
      }
      return true;
    }
    if (this.state.currentStep === this.getStep(2)) {
      let hasError = false;
      const annotations = this.state.annotations;
      if (this.state.sendType === SendType.invite) {
        this.state.recipients.forEach((item: any) => {
          const hasSignature = annotations?.some(
            (it: any) => it.data?.type === 'signature' && isSameUser(it.data, item)
          );
          if (!hasSignature) {
            hasError = true;
            toast({
              title: 'Error',
              description: `Missing ${
                item.name || shortenWalletAddress(item.walletAddress, 'shorter') || item.email
              }‘s ${!hasSignature ? 'signature' : 'date'} field`,
              variant: 'error'
            });
          }
        });
      } else {
        if (this.state.contractType === SIGN_MODE_ENUM.OneSignOne) {
          const hasSignature = annotations?.some((it: any) => it.data.type === 'signature');
          if (!hasSignature) {
            hasError = true;
            toast({
              title: 'Error',
              description: `Missing  ${!hasSignature ? 'signature' : 'date'} field`,
              variant: 'error'
            });
          }
        } else {
          hasError = false;
        }
      }
      if (hasError) return false;
      return true;
    }

    if (this.state.currentStep === this.getStep(3)) {
      let errorMsg = '';
      if (!this.state.contractName) {
        errorMsg = 'Please input contract name';
      }
      if (this.state.contractName.length > 200) {
        errorMsg = 'Contract name should not exceed 200 characters.';
      }
      if (this.state.sendType === SendType.link && !this.state.expireDate) {
        this.setExpireTimeError(true);
        errorMsg = 'Please select expire date';
      } else {
        this.setExpireTimeError(false);
      }
      if (this.state.enablePassword && !this.state.password) {
        errorMsg = 'Please input password';
      }
      if (this.state.sendType === SendType.invite) {
        if (this.state.enableExpireDate) {
          if (!this.state.expireDate) {
            errorMsg = 'Please select expire date';
          } else if (new Date(this.state.expireDate) <= new Date()) {
            errorMsg = 'Please select a future date';
          }
        }
        if (this.state.recipients?.length < 1) {
          errorMsg = 'Please add at least one signer';
        }
        if (!this.isValidateSigners(this.state.recipients)) {
          errorMsg = 'Please add correct email or wallet address and network for all signers';
        }
        if (!this.validateDuplicateSigners(this.state.recipients)) {
          return false;
        }
      }
      if (errorMsg) {
        toast({
          title: 'Error',
          description: errorMsg,
          variant: 'error'
        });
        return false;
      }
      if (this.state.sendType !== 'link' && !this.state.enablePassword) {
        this.setPasswordModalVisible(true);
        return false;
      } else {
        return true;
      }
    }
  };

  getContractTemplate = async (id: string): Promise<any> => {
    this.state.loading = true;
    try {
      const res = await queryContractTemplate(id);
      const { contentHtml, fieldsStr, signerFieldsStr, tags, name } = res || {};
      const fields = (fieldsStr && JSON.parse(fieldsStr)) || [];
      this.state.loading = false;
      const signers = (signerFieldsStr && JSON.parse(signerFieldsStr)) || [];
      return {
        templateData: {
          html: contentHtml,
          fields,
          signers: signers
        },
        contractType: tags === SendType.invite ? undefined : tags,
        sendType: tags === SendType.invite ? SendType.invite : SendType.link,
        contractName: name,
        recipients: signers.map((it: any) => ({
          signerId: it.id
        }))
      };
    } catch (error) {
      this.state.loading = false;
    }
  };
  generatePdf = async (html: string): Promise<any> => {
    const wrapHtml = getWrapHtml(html);
    const wrapHtmlBlank = wrapHtml
      .replace(/{{\S+?}}/gi, (substring) => {
        return Array(substring.length).fill('_').join('');
      })
      .replaceAll('font-family:monospace;', '');

    return Promise.all([wrapHtml, wrapHtmlBlank].map((item) => html2pdf(item, this.state.contractName)));
  };
}
